import React, { useState, useMemo, useEffect } from "react";
import {
  Row,
  Col,
  Typography,
  Card,
  Table,
  Tag,
  Space,
} from "antd";
import {
  UserOutlined,
  RiseOutlined,
  CrownOutlined,
  RocketOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import moment from "moment";
import axios from "axios";
import ENVIRONMENT from "../../../../environments";
import styles from "./AdminAICharts.module.css";
import FreeTrialsChart from "./FreeTrialsChart";
import ConversionsChart from "./ConversionsChart";
import ImageGenerationsChart from "./ImageGenerationsChart";
import DateRangePicker from "../../DateRangePicker/DateRangePicker";

const { Title, Text } = Typography;
const DATE_FORMAT = "YYYY/MM/DD";

function AdminAICharts() {
  const chartStyle = useMemo(() => ({ 
    padding: 20, 
    background: "#fff", 
    borderRadius: 8 
  }), []);

  const [dateRange, setDateRange] = useState({
    start: moment().subtract(6, 'days'),
    end: moment()
  });

  const handleDateChange = (dateValues) => {
    if (dateValues && dateValues[0] && dateValues[1]) {
      setDateRange({
        start: dateValues[0],
        end: dateValues[1]
      });
    }
  };

  // State for subscription data
  const [subscriptionData, setSubscriptionData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Icon mapping for subscription types
  const getIconForSubscription = (typeName) => {
    const lowerName = typeName.toLowerCase();
    if (lowerName.includes('free trial')) return <UserOutlined />;
    if (lowerName.includes('starter')) return <RocketOutlined />;
    if (lowerName.includes('growth')) return <RiseOutlined />;
    if (lowerName.includes('pro')) return <CrownOutlined />;
    if (lowerName.includes('basic') || lowerName.includes('bundle')) return <TrophyOutlined />;
    return <UserOutlined />;
  };

  // Fetch active users data
  const fetchActiveUsersData = async () => {
    try {
      setIsLoading(true);
      const payload = {
        action: "get_active_users_count"
      };

      const response = await axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, payload);
      const data = response.data || [];
      
      // Transform API data to table format
      const transformedData = data.map((item, index) => ({
        key: index.toString(),
        subscription: item.type_name,
        activeUsers: parseInt(item.count, 10),
        icon: getIconForSubscription(item.type_name),
      }));

      setSubscriptionData(transformedData);
    } catch (error) {
      console.error("Error fetching active users data:", error);
      setSubscriptionData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchActiveUsersData();
  }, []);

  const columns = [
    {
      title: 'Subscription Plan',
      dataIndex: 'subscription',
      key: 'subscription',
      render: (text, record) => (
        <Space>
          <span style={{ fontSize: '16px', color: '#1890ff' }}>
            {record.icon}
          </span>
          <Text strong style={{ fontSize: '14px' }}>{text}</Text>
        </Space>
      ),
    },
    {
      title: 'Active Users',
      dataIndex: 'activeUsers',
      key: 'activeUsers',
      align: 'center',
      render: (value) => (
        <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
          {value.toLocaleString()}
        </Text>
      ),
    },
  ];

  return (
    <Row gutter={[24, 24]}>
      <Col span={24}>
        <Card style={{ ...chartStyle, marginBottom: 0 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={4} style={{ margin: 0 }}>
                Active Subscriptions Overview
              </Title>
              <Text type="secondary" style={{ fontSize: '13px' }}>
                Real-time subscription and user metrics
              </Text>
            </Col>
          </Row>
        </Card>
      </Col>

      <Col span={24}>
        <Card style={chartStyle}>
          <Title level={5} style={{ marginTop: 0 }}>
            Active Users by Subscription
          </Title>
          <Table
            columns={columns}
            dataSource={subscriptionData}
            pagination={false}
            size="middle"
            style={{ marginTop: 16 }}
            loading={isLoading}
          />
        </Card>
      </Col>

      <FreeTrialsChart 
        chartStyle={chartStyle} 
        dateRange={dateRange}
        onDateChange={handleDateChange}
      />

      <ConversionsChart 
        chartStyle={chartStyle} 
        dateRange={dateRange}
        onDateChange={handleDateChange}
      />

      <ImageGenerationsChart 
        chartStyle={chartStyle} 
        dateRange={dateRange}
        onDateChange={handleDateChange}
      />
    </Row>
  );
}

export default AdminAICharts;